// Settings → Branding tab.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   tab button:         line 2753  (#settings-panel-branding)
//   renderBrandingPanel line 9315
//   buildPreview        line 9388
//   defaultBranding     line 8639
//   applyBranding       line 8652  (writes --orange / --orange-2 CSS vars)
//   localStorage key:   'kos_branding_v1'
//
// Backend
// -------
// There is no retailer-facing `/api/portal/settings/branding` endpoint today
// (verified against /var/www/kitchenos-api/server.js as of 2026-05). The
// `/api/wizard/branding/:retailerId` route exists for the Wizard Branding
// tab but that's a different model. We POST the desired shape and fall back
// gracefully on 404 — the values are also written to localStorage so other
// modules in the rebuilt portal can pick them up.
//
//   TODO: PUT /api/portal/settings/branding  { businessName, primary, secondary, logo }
//   TODO: GET /api/portal/settings/branding  → { branding }

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";
import { dispatchBrandingChanged } from "../../../shell/branding";

import "./branding.css";

// ── Types & defaults ─────────────────────────────────────────────────────────

interface BrandingState {
  businessName: string;
  primary: string;
  secondary: string;
  /** Data URL (or empty string). Object-URL upload path produces a data URL
   *  so the value can be saved verbatim to localStorage / sent to the server. */
  logo: string;
}

const LS_KEY = "tkos.branding";
const LS_KEY_LEGACY = "kos_branding_v1";
const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_LOGO_TYPES = new Set(["image/png", "image/svg+xml", "image/jpeg", "image/webp"]);

// Must stay in step with shell/branding.ts PLATFORM_BRAND_NAME: "reset to
// defaults" writes this into the retailer's profile, and the topbar renders
// anything the profile supplies as a RETAILER name (single tone), so a stale
// value here would put the old product name in the chrome as if a retailer
// had chosen it.
const DEFAULTS: BrandingState = {
  businessName: "Kitchengine",
  primary: "#F59E0B",
  secondary: "#1F2937",
  logo: "",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isHex(v: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(v);
}

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function loadFromStorage(): BrandingState {
  const out: BrandingState = { ...DEFAULTS };
  for (const key of [LS_KEY, LS_KEY_LEGACY]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const p = parsed as Partial<BrandingState>;
        if (typeof p.businessName === "string") out.businessName = p.businessName;
        if (typeof p.primary === "string" && isHex(p.primary)) out.primary = p.primary;
        if (typeof p.secondary === "string" && isHex(p.secondary)) out.secondary = p.secondary;
        if (typeof p.logo === "string") out.logo = p.logo;
      }
    } catch {
      // ignore corrupted storage
    }
  }
  return out;
}

function saveToStorage(s: BrandingState): void {
  try {
    const json = JSON.stringify(s);
    localStorage.setItem(LS_KEY, json);
    localStorage.setItem(LS_KEY_LEGACY, json);
  } catch {
    // Quota / SecurityError — large data-URL logos may bust storage; swallow.
  }
}

// ── Tab definition ───────────────────────────────────────────────────────────

const brandingTab: SettingsTab = {
  id: "branding",
  label: "Branding",
  icon: iconMarkup("palette", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;

    /** Object URLs created for preview thumbnails — revoked on cleanup. */
    const objectUrls = new Set<string>();
    /** Currently mounted FileReader — aborted on cleanup. */
    let activeReader: FileReader | null = null;

    const state: BrandingState = loadFromStorage();

    // ── Scaffold ──────────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "set-brand-root";
    root.innerHTML = `
      <div class="set-brand-grid">
        <!-- Left column: controls -->
        <div class="set-brand-controls">

          <section class="set-section set-brand-section">
            <h3 class="set-section-title">Business identity</h3>
            <p class="set-help">
              Your logo, business name and colours flow through the retailer
              portal and the customer portal automatically.
            </p>

            <div class="set-field">
              <label for="set-brand-name">Business name</label>
              <input
                id="set-brand-name"
                class="set-input"
                type="text"
                value="${esc(state.businessName)}"
                data-role="name"
                maxlength="80"
                autocomplete="organization"
              />
            </div>

            <div class="set-field">
              <label>Business logo</label>
              <div
                class="set-brand-drop"
                data-role="drop"
                tabindex="0"
                role="button"
                aria-label="Upload business logo"
              >
                <div class="set-brand-drop__preview" data-role="logo-preview">
                  ${
                    state.logo
                      ? `<img src="${esc(state.logo)}" alt="Business logo">`
                      : `
                    ${iconMarkup("upload", { size: "lg" })}
                    <div class="set-brand-drop__hint">
                      Click or drop to upload (PNG / SVG, max 2 MB)
                    </div>
                  `
                  }
                </div>
                <input
                  type="file"
                  class="set-brand-drop__input"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  data-role="logo-file"
                  hidden
                />
              </div>
              <button
                type="button"
                class="set-btn set-btn-secondary set-brand-remove-btn"
                data-action="remove-logo"
                ${state.logo ? "" : "hidden"}
              >Remove logo</button>
            </div>
          </section>

          <section class="set-section set-brand-section">
            <h3 class="set-section-title">Brand colours</h3>
            <p class="set-help">
              Pick the primary and secondary colours used throughout the
              customer portal — buttons, links, accents and highlights all
              follow.
            </p>

            <div class="set-brand-colour-row">
              <span
                class="set-brand-swatch"
                data-role="primary-swatch"
                style="background:${esc(state.primary)}"
                aria-hidden="true"
              ></span>
              <div class="set-brand-colour-row__main">
                <label for="set-brand-primary">Primary colour</label>
                <div class="set-brand-colour-row__inputs">
                  <input
                    id="set-brand-primary"
                    type="color"
                    class="set-brand-colour-picker"
                    value="${esc(state.primary)}"
                    data-role="primary"
                  />
                  <input
                    type="text"
                    class="set-input set-brand-hex"
                    value="${esc(state.primary)}"
                    data-role="primary-hex"
                    aria-label="Primary colour hex code"
                    maxlength="7"
                  />
                </div>
              </div>
            </div>

            <div class="set-brand-colour-row">
              <span
                class="set-brand-swatch"
                data-role="secondary-swatch"
                style="background:${esc(state.secondary)}"
                aria-hidden="true"
              ></span>
              <div class="set-brand-colour-row__main">
                <label for="set-brand-secondary">Secondary colour</label>
                <div class="set-brand-colour-row__inputs">
                  <input
                    id="set-brand-secondary"
                    type="color"
                    class="set-brand-colour-picker"
                    value="${esc(state.secondary)}"
                    data-role="secondary"
                  />
                  <input
                    type="text"
                    class="set-input set-brand-hex"
                    value="${esc(state.secondary)}"
                    data-role="secondary-hex"
                    aria-label="Secondary colour hex code"
                    maxlength="7"
                  />
                </div>
              </div>
            </div>

            <div class="set-brand-actions">
              <button type="button" class="set-btn set-btn-secondary" data-action="reset">
                Reset to defaults
              </button>
              <button type="button" class="set-btn" data-action="save">
                Save changes
              </button>
            </div>
          </section>
        </div>

        <!-- Right column: live preview -->
        <aside class="set-brand-preview-col" aria-label="Live preview">
          <div class="set-section set-brand-section set-brand-preview-card">
            <h3 class="set-section-title">Live preview</h3>
            <p class="set-help">
              A mini mockup of how your customer-facing portal will look.
            </p>
            <div
              class="set-brand-preview"
              data-role="preview"
              style="--brand-primary:${esc(state.primary)};--brand-secondary:${esc(state.secondary)}"
            >${renderPreviewBody(state)}</div>
            <p class="set-help set-brand-preview-tip">
              Tip: changes here apply immediately to the live customer portal
              once you save.
            </p>
          </div>
        </aside>
      </div>
    `;
    container.appendChild(root);

    // ── Element refs ──────────────────────────────────────────────────────

    const nameInput = root.querySelector<HTMLInputElement>("[data-role='name']")!;
    const dropEl = root.querySelector<HTMLElement>("[data-role='drop']")!;
    const logoFileInput = root.querySelector<HTMLInputElement>(
      "[data-role='logo-file']",
    )!;
    const logoPreviewEl = root.querySelector<HTMLElement>(
      "[data-role='logo-preview']",
    )!;
    const removeBtn = root.querySelector<HTMLButtonElement>(
      "[data-action='remove-logo']",
    )!;
    const primaryInput = root.querySelector<HTMLInputElement>(
      "[data-role='primary']",
    )!;
    const primaryHex = root.querySelector<HTMLInputElement>(
      "[data-role='primary-hex']",
    )!;
    const primarySwatch = root.querySelector<HTMLElement>(
      "[data-role='primary-swatch']",
    )!;
    const secondaryInput = root.querySelector<HTMLInputElement>(
      "[data-role='secondary']",
    )!;
    const secondaryHex = root.querySelector<HTMLInputElement>(
      "[data-role='secondary-hex']",
    )!;
    const secondarySwatch = root.querySelector<HTMLElement>(
      "[data-role='secondary-swatch']",
    )!;
    const previewEl = root.querySelector<HTMLElement>("[data-role='preview']")!;
    const resetBtn = root.querySelector<HTMLButtonElement>(
      "[data-action='reset']",
    )!;
    const saveBtn = root.querySelector<HTMLButtonElement>(
      "[data-action='save']",
    )!;

    // ── Renderers ─────────────────────────────────────────────────────────

    function renderPreviewBody(s: BrandingState): string {
      const businessName = s.businessName || "Your Business";
      const logoHtml = s.logo
        ? `<img src="${esc(s.logo)}" alt="" class="set-brand-preview__logo-img">`
        : "";
      return `
        <div class="set-brand-preview__bar">
          <div class="set-brand-preview__brand">
            ${logoHtml}
            <span class="set-brand-preview__brand-name">${esc(businessName)}</span>
          </div>
          <div class="set-brand-preview__chip">Customer Portal</div>
        </div>
        <div class="set-brand-preview__body">
          <div class="set-brand-preview__greet">Hi Sarah, welcome back</div>
          <div class="set-brand-preview__cards">
            <div class="set-brand-preview__card">
              <div class="set-brand-preview__card-title">Your project</div>
              <div class="set-brand-preview__card-value">Approved</div>
            </div>
            <div class="set-brand-preview__card">
              <div class="set-brand-preview__card-title">Install date</div>
              <div class="set-brand-preview__card-value">14 May</div>
            </div>
          </div>
          <div class="set-brand-preview__quote">
            <div>
              <div class="set-brand-preview__quote-title">Your kitchen quote</div>
              <div class="set-brand-preview__quote-meta">Ready to view · £28,400</div>
            </div>
            <button class="set-brand-preview__btn" type="button">Open quote</button>
          </div>
          <div class="set-brand-preview__links">
            <span class="set-brand-preview__link">View documents ›</span>
            <span class="set-brand-preview__link">Message us ›</span>
          </div>
        </div>
      `;
    }

    function refreshPreview(): void {
      previewEl.style.setProperty("--brand-primary", state.primary);
      previewEl.style.setProperty("--brand-secondary", state.secondary);
      previewEl.innerHTML = renderPreviewBody(state);
    }

    function refreshLogoPreview(): void {
      if (state.logo) {
        logoPreviewEl.innerHTML = `<img src="${esc(state.logo)}" alt="Business logo">`;
        removeBtn.hidden = false;
      } else {
        logoPreviewEl.innerHTML = `
          ${iconMarkup("upload", { size: "lg" })}
          <div class="set-brand-drop__hint">
            Click or drop to upload (PNG / SVG, max 2 MB)
          </div>
        `;
        removeBtn.hidden = true;
      }
    }

    // ── Event handlers ────────────────────────────────────────────────────

    function onNameInput(): void {
      state.businessName = nameInput.value;
      saveToStorage(state);
      refreshPreview();
    }

    function applyColourChange(
      which: "primary" | "secondary",
      value: string,
    ): void {
      if (!isHex(value)) return;
      state[which] = value;
      saveToStorage(state);
      const picker = which === "primary" ? primaryInput : secondaryInput;
      const hex = which === "primary" ? primaryHex : secondaryHex;
      const swatch = which === "primary" ? primarySwatch : secondarySwatch;
      if (picker.value.toLowerCase() !== value.toLowerCase()) picker.value = value;
      if (hex.value.toLowerCase() !== value.toLowerCase()) hex.value = value;
      swatch.style.background = value;
      previewEl.style.setProperty(`--brand-${which}`, value);
    }

    function onPrimaryPicker(): void {
      applyColourChange("primary", primaryInput.value);
    }
    function onPrimaryHex(): void {
      const v = primaryHex.value.trim();
      if (isHex(v)) applyColourChange("primary", v);
    }
    function onSecondaryPicker(): void {
      applyColourChange("secondary", secondaryInput.value);
    }
    function onSecondaryHex(): void {
      const v = secondaryHex.value.trim();
      if (isHex(v)) applyColourChange("secondary", v);
    }

    function onDropClick(): void {
      logoFileInput.click();
    }
    function onDropKeydown(ev: KeyboardEvent): void {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        logoFileInput.click();
      }
    }
    function onDropDragover(ev: DragEvent): void {
      ev.preventDefault();
      dropEl.classList.add("is-dragover");
    }
    function onDropDragleave(): void {
      dropEl.classList.remove("is-dragover");
    }
    function onDropDrop(ev: DragEvent): void {
      ev.preventDefault();
      dropEl.classList.remove("is-dragover");
      const file = ev.dataTransfer?.files?.[0];
      if (file) processLogoFile(file);
    }
    function onLogoFileChange(): void {
      const file = logoFileInput.files?.[0];
      if (file) processLogoFile(file);
      // Reset the input so the same file can be re-selected if needed.
      logoFileInput.value = "";
    }

    function processLogoFile(file: File): void {
      if (!ALLOWED_LOGO_TYPES.has(file.type)) {
        ctx.showToast(
          "Logo must be PNG, SVG, JPEG or WebP",
          "error",
        );
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        ctx.showToast("Logo must be under 2 MB", "error");
        return;
      }

      // Cancel any in-flight read.
      if (activeReader) {
        try { activeReader.abort(); } catch { /* noop */ }
        activeReader = null;
      }

      const reader = new FileReader();
      activeReader = reader;
      reader.onload = (): void => {
        if (disposed) return;
        if (reader !== activeReader) return; // superseded
        const dataUrl = String(reader.result ?? "");
        if (!dataUrl) return;
        state.logo = dataUrl;
        saveToStorage(state);
        refreshLogoPreview();
        refreshPreview();
        ctx.showToast("Logo uploaded", "success");
        activeReader = null;
      };
      reader.onerror = (): void => {
        if (disposed) return;
        if (reader !== activeReader) return;
        ctx.showToast("Couldn't read logo file", "error");
        activeReader = null;
      };
      reader.readAsDataURL(file);
    }

    function onRemoveLogo(): void {
      state.logo = "";
      saveToStorage(state);
      refreshLogoPreview();
      refreshPreview();
    }

    function onReset(): void {
      void (async () => {
        if (!(await confirmDialog("Reset branding to The Kitchen OS defaults?", {
          title: "Reset branding",
          confirmLabel: "Reset",
          destructive: true,
        }))) return;
        Object.assign(state, DEFAULTS);
        saveToStorage(state);
        nameInput.value = state.businessName;
        primaryInput.value = state.primary;
        primaryHex.value = state.primary;
        primarySwatch.style.background = state.primary;
        secondaryInput.value = state.secondary;
        secondaryHex.value = state.secondary;
        secondarySwatch.style.background = state.secondary;
        refreshLogoPreview();
        refreshPreview();
        ctx.showToast("Branding reset to defaults", "info");
      })();
    }

    async function onSave(): Promise<void> {
      saveBtn.disabled = true;
      // PUT /api/portal/settings/branding — endpoint wired 2026-05-19;
      // persists business_name / primary_colour / secondary_colour /
      // logo_url to the `retailers` row and invalidates the branding
      // cache so the customer portal sees the change on its next fetch.
      try {
        await ctx.api("/portal/settings/branding", {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({
            businessName: state.businessName,
            primary: state.primary,
            secondary: state.secondary,
            logo: state.logo,
          }),
        });
        if (disposed) return;
        // Tell the shell to re-fetch + re-apply branding immediately so
        // the topbar logo / name / colour update live without a refresh.
        dispatchBrandingChanged();
        ctx.showToast("Branding saved", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        // NOTE: this endpoint (PUT /api/portal/settings/branding) has been live
        // since 2026-05-19, so a 404/501 here is a REAL failure — most often the
        // session not being bound to a retailer (no req.portalRetailerId) — NOT
        // a "pending" endpoint. Previously we masked it as "saved locally", which
        // made a failed save look successful and the change silently vanish.
        // Always surface the failure so the colour/logo never appears saved when
        // it wasn't. (2026-06-10)
        const status = err instanceof ApiError ? err.status : 0;
        ctx.showToast(
          status === 401 || status === 404
            ? "Couldn't save branding — your session may have expired. Please sign in again and retry."
            : "Couldn't save branding. Please try again.",
          "error",
        );
      } finally {
        if (!disposed) saveBtn.disabled = false;
      }
    }

    // ── Wire-up ───────────────────────────────────────────────────────────

    nameInput.addEventListener("input", onNameInput);
    primaryInput.addEventListener("input", onPrimaryPicker);
    primaryHex.addEventListener("input", onPrimaryHex);
    secondaryInput.addEventListener("input", onSecondaryPicker);
    secondaryHex.addEventListener("input", onSecondaryHex);

    dropEl.addEventListener("click", onDropClick);
    dropEl.addEventListener("keydown", onDropKeydown);
    dropEl.addEventListener("dragover", onDropDragover);
    dropEl.addEventListener("dragleave", onDropDragleave);
    dropEl.addEventListener("drop", onDropDrop);
    logoFileInput.addEventListener("change", onLogoFileChange);

    removeBtn.addEventListener("click", onRemoveLogo);
    resetBtn.addEventListener("click", onReset);
    saveBtn.addEventListener("click", () => void onSave());

    // ── Hydrate from server (post-mount) ──────────────────────────────────
    //
    // The form was seeded from localStorage. After the chrome is on
    // screen we fetch the authoritative DB state and overwrite the
    // inputs with what's actually saved. Fire-and-forget; if the
    // network is down we keep the localStorage draft.
    void (async (): Promise<void> => {
      interface ProfileResp {
        ok?: boolean;
        profile?: {
          businessName?: string;
          logoUrl?: string;
          primaryColour?: string;
          secondaryColour?: string;
        };
      }
      try {
        const resp = await ctx.api<ProfileResp>("/portal/retailer-profile", {
          signal: ac.signal,
        });
        if (disposed || !resp?.profile) return;
        const p = resp.profile;
        if (typeof p.businessName === "string" && p.businessName.trim()) {
          state.businessName = p.businessName.trim();
          nameInput.value = state.businessName;
        }
        if (typeof p.primaryColour === "string" && isHex(p.primaryColour)) {
          state.primary = p.primaryColour;
          primaryInput.value = state.primary;
          primaryHex.value = state.primary;
          primarySwatch.style.background = state.primary;
        }
        if (typeof p.secondaryColour === "string" && isHex(p.secondaryColour)) {
          state.secondary = p.secondaryColour;
          secondaryInput.value = state.secondary;
          secondaryHex.value = state.secondary;
          secondarySwatch.style.background = state.secondary;
        }
        if (typeof p.logoUrl === "string" && p.logoUrl !== state.logo) {
          state.logo = p.logoUrl;
          refreshLogoPreview();
        }
        saveToStorage(state);
        refreshPreview();
      } catch (err) {
        if (isAbortError(err)) return;
        // 404 / 503 / network — keep the localStorage draft visible.
      }
    })();

    // ── Cleanup ────────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      nameInput.removeEventListener("input", onNameInput);
      primaryInput.removeEventListener("input", onPrimaryPicker);
      primaryHex.removeEventListener("input", onPrimaryHex);
      secondaryInput.removeEventListener("input", onSecondaryPicker);
      secondaryHex.removeEventListener("input", onSecondaryHex);

      dropEl.removeEventListener("click", onDropClick);
      dropEl.removeEventListener("keydown", onDropKeydown);
      dropEl.removeEventListener("dragover", onDropDragover);
      dropEl.removeEventListener("dragleave", onDropDragleave);
      dropEl.removeEventListener("drop", onDropDrop);
      logoFileInput.removeEventListener("change", onLogoFileChange);

      removeBtn.removeEventListener("click", onRemoveLogo);
      resetBtn.removeEventListener("click", onReset);

      if (activeReader) {
        try { activeReader.abort(); } catch { /* noop */ }
        activeReader = null;
      }

      for (const url of objectUrls) URL.revokeObjectURL(url);
      objectUrls.clear();

      ac.abort();
    };
  },
};

export default brandingTab;
